import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import axios from 'axios';
import { api } from '../api/client';

export type UserType = 'client' | 'staff';
export type UserRole = 'client' | 'agent' | 'admin';

export interface AuthUser {
  id: string;
  email: string;
  full_name: string;
  type: UserType;
  role: UserRole;
  is_active: boolean;
  organization_id: string | null;
  support_lines: number[];
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const STORAGE_KEY = 'sd.auth';

interface StoredAuth {
  token: string;
  refresh_token?: string;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  // Ref'ы для interceptor'а (актуальное значение без переподписки)
  const tokenRef = useRef<string | null>(null);
  const refreshTokenRef = useRef<string | null>(null);
  const refreshInflight = useRef<Promise<string | null> | null>(null);
  tokenRef.current = token;
  refreshTokenRef.current = refreshToken;

  const persist = (access: string | null, refresh: string | null) => {
    if (access) localStorage.setItem(STORAGE_KEY, JSON.stringify({
      token: access, refresh_token: refresh ?? undefined,
    } satisfies StoredAuth));
    else localStorage.removeItem(STORAGE_KEY);
  };

  const setSession = (access: string | null, refresh: string | null) => {
    setToken(access);
    setRefreshToken(refresh);
    tokenRef.current = access;
    refreshTokenRef.current = refresh;
    persist(access, refresh);
  };

  // Восстановление сессии
  useEffect(() => {
    (async () => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw) as StoredAuth;
        if (!parsed.token) return;
        const r = await api.get<AuthUser>('/api/v1/auth/me',
          { headers: { Authorization: `Bearer ${parsed.token}` } });
        setToken(parsed.token);
        setRefreshToken(parsed.refresh_token ?? null);
        tokenRef.current = parsed.token;
        refreshTokenRef.current = parsed.refresh_token ?? null;
        setUser(r.data);
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Axios-interceptor: Authorization + auto-refresh при 401.
  useEffect(() => {
    const reqId = api.interceptors.request.use(cfg => {
      const t = tokenRef.current;
      if (t) cfg.headers.Authorization = `Bearer ${t}`;
      return cfg;
    });
    const resId = api.interceptors.response.use(
      r => r,
      async err => {
        const status = err?.response?.status;
        const original = err?.config;
        const rt = refreshTokenRef.current;
        // Условие: 401, есть refresh, не сам /refresh, не пере-retry'или
        const isRefreshCall = original?.url?.includes('/api/v1/auth/refresh')
                           || original?.url?.includes('/api/v1/auth/login');
        if (status === 401 && rt && !original?._retry && !isRefreshCall) {
          original._retry = true;
          try {
            // Coalesce: если уже идёт refresh — ждём его
            if (!refreshInflight.current) {
              refreshInflight.current = (async () => {
                const resp = await axios.post(
                  '/api/v1/auth/refresh', { refresh_token: rt },
                  { headers: { 'Content-Type': 'application/json' } },
                );
                const { access_token, refresh_token } = resp.data;
                setSession(access_token, refresh_token ?? rt);
                return access_token as string;
              })().finally(() => { refreshInflight.current = null; });
            }
            const newAccess = await refreshInflight.current;
            if (newAccess) {
              original.headers = original.headers ?? {};
              original.headers.Authorization = `Bearer ${newAccess}`;
              return api.request(original);
            }
          } catch {
            // refresh не удался — logout
            setSession(null, null);
            setUser(null);
          }
        } else if (status === 401 && tokenRef.current && !isRefreshCall) {
          // 401 без refresh-токена → обычный logout
          setSession(null, null);
          setUser(null);
        }
        return Promise.reject(err);
      },
    );
    return () => {
      api.interceptors.request.eject(reqId);
      api.interceptors.response.eject(resId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email: string, password: string) => {
    // OAuth2 password flow → form-urlencoded.
    const form = new URLSearchParams({ username: email, password });
    const r = await api.post('/api/v1/auth/login', form, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    const { access_token, refresh_token, user: u } = r.data;
    setSession(access_token, refresh_token ?? null);
    setUser(u);
  };

  const logout = () => {
    const rt = refreshTokenRef.current;
    if (rt) {
      // best-effort revoke на сервере
      api.post('/api/v1/auth/logout', { refresh_token: rt }).catch(() => {});
    }
    setSession(null, null);
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be inside <AuthProvider>');
  return ctx;
}
